<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6 overflow-hidden">
    <div class="bg-white rounded-lg shadow-xl flex flex-col max-h-[90vh] paper-width scale-paper">
      <!-- Header -->
      <div class="bg-gray-50 px-8 py-4 rounded-t-lg border-b flex justify-between items-center flex-shrink-0">
        <div class="flex items-center gap-4">
          <img src="/OC_print_logo_icon_color.svg" alt="Logo" class="h-8 w-8">
          <div>
            <h2 class="text-xl font-bold text-gray-800">Invoice</h2>
            <p class="text-sm text-gray-500">Invoice #{{ invoice.id }}</p>
          </div>
        </div>
        <div class="flex items-center gap-4">
          <button 
            class="text-gray-400 hover:text-gray-600"
            @click="$emit('download', invoice)"
          >
            <i class="fas fa-download text-xl"></i>
          </button>
          <button @click="$emit('close')" class="text-gray-500 hover:text-gray-700">
            <i class="fas fa-times text-xl"></i>
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto">
        <div class="paper-min-height bg-white p-8">
          <!-- Top Section -->
          <div class="grid grid-cols-2 gap-x-16 mb-12">
            <!-- Left Side -->
            <div>
              <h3 class="text-gray-700 font-medium mb-3">Bill To:</h3>
              <div class="space-y-4">
                <p class="text-gray-800">{{ invoice.userName }}</p>
                <div class="flex flex-col items-start gap-2">
                  <div>Address:</div>
                  <!-- <i class="fas fa-credit-card text-gray-400"></i> -->
                  <span> {{ invoice.cardLast4 }}</span>
                </div>
              </div>
            </div>

            <!-- Right Side -->
            <div class="space-y-4">
              <div class="flex justify-between mb-2">
                <span class="text-gray-600">Invoice Date:</span>
                <span>{{ formattedInvoiceDate }}</span>
              </div>
              <div class="flex justify-between mb-2">
                <span class="text-gray-600">Due Date:</span>
                <span>{{ formattedDueDate }}</span>
              </div>
              <div class="flex justify-between mb-2">
                <span class="text-gray-600">Days Left:</span>
                <span>{{ daysLeft }} days</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Status:</span>
                <span 
                  class="px-2 py-1 rounded-full text-sm"
                  :class="{
                    'bg-green-100 text-green-700': invoice.status === 'paid',
                    'bg-yellow-100 text-yellow-700': invoice.status === 'pending',
                    'bg-red-100 text-red-700': invoice.status === 'failed'
                  }"
                >
                  {{ invoice.status }}
                </span>
              </div>
            </div>
          </div>

          <!-- Invoice Items Table -->
          <div class="bg-white rounded-lg border mb-12">
            <table class="w-full">
              <thead class="bg-gray-50 border-b">
                <tr>
                  <th class="py-3 px-4 text-left text-gray-600">Description</th>
                  <th class="py-3 px-4 text-center text-gray-600 w-24">Qty</th>
                  <th class="py-3 px-4 text-right text-gray-600 w-32">Price</th>
                  <th class="py-3 px-4 text-right text-gray-600 w-32">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in invoiceItems" :key="index" class="border-b">
                  <td class="py-3 px-4">
                    <div class="text-gray-800">{{ item.description }}</div>
                    <div class="text-sm text-gray-500">{{ item.details }}</div>
                  </td>
                  <td class="py-3 px-4 text-center">{{ item.quantity }}</td>
                  <td class="py-3 px-4 text-right">€{{ item.price.toFixed(2) }}</td>
                  <td class="py-3 px-4 text-right">€{{ (item.quantity * item.price).toFixed(2) }}</td>
                </tr>
              </tbody>
              <tfoot class="bg-gray-50">
                <tr>
                  <td colspan="3" class="py-3 px-4 text-right font-medium">Subtotal</td>
                  <td class="py-3 px-4 text-right">€{{ subtotal.toFixed(2) }}</td>
                </tr>
                <tr>
                  <td colspan="3" class="py-3 px-4 text-right font-medium">VAT (0%)</td>
                  <td class="py-3 px-4 text-right">€0.00</td>
                </tr>
                <tr class="font-bold">
                  <td colspan="3" class="py-3 px-4 text-right">Total</td>
                  <td class="py-3 px-4 text-right text-pink-500">€{{ subtotal.toFixed(2) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- Payment Information -->
          <div class="grid grid-cols-2 gap-x-16 mb-12">
            <div class="bg-gray-50 p-4 rounded-lg">
              <h3 class="font-medium text-gray-700 mb-3">Bank Transfer Details:</h3>
              <div class="space-y-2 text-gray-600">
                <p>Account Name: Odds Composer OÜ</p>
                <p>IBAN: EE601010220302953227</p>
                <p>SWIFT/BIC: EEUHEE2X</p>
                <p>Bank: SEB Pank</p>
              </div>
            </div>
            <div class="bg-gray-50 p-4 rounded-lg">
              <h3 class="font-medium text-gray-700 mb-3">Payment Reference:</h3>
              <p class="text-gray-600">Please include the invoice number in the payment reference</p>
              <p class="text-gray-600 mt-2">Payment due within {{ daysLeft }} days</p>
            </div>
          </div>

          <!-- VAT Information -->
          <div class="bg-gray-50 p-4 rounded-lg mb-12 text-gray-600 text-sm">
            <p>This transaction is subject to the reverse charge mechanism under Article 196 of Council Directive 2006/112/EC.</p>
            <p>The liability to account for and pay VAT is shifted to the recipient of the services.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  invoice: {
    type: Object,
    required: true
  }
})

defineEmits(['close', 'download'])

// Convert invoice data to items format if not present
const invoiceItems = computed(() => {
  if (props.invoice.items) {
    return props.invoice.items
  }
  // Create default item from invoice data
  return [{
    description: props.invoice.fixtureName,
    details: `${props.invoice.fixtureName}\n${props.invoice.league}`,
    quantity: 1,
    price: props.invoice.amount
  }]
})

// Calculate subtotal
const subtotal = computed(() => {
  return invoiceItems.value.reduce((sum, item) => {
    return sum + (item.quantity * item.price)
  }, 0)
})

const formatDate = (dateString) => {
  try {
    if (!dateString) return ''  // Return empty string instead of current date
    
    // Handle datetime-local input format (ISO string)
    if (dateString.includes('T')) {
      const date = new Date(dateString)
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('de-DE', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        })
      }
    }
    
    // Handle German format (DD.MM.YYYY HH:mm or DD.MM.YYYY)
    if (dateString.includes('.')) {
      const parts = dateString.split(' ')
      return parts[0] // Return just the date part
    }
    
    // Try parsing as regular date
    const date = new Date(dateString)
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    }
    
    return dateString // Return original string if parsing fails
  } catch (e) {
    console.error('Error formatting date:', e)
    return dateString // Return original string if error occurs
  }
}

// Update the computed properties
const formattedInvoiceDate = computed(() => {
  return formatDate(props.invoice.purchaseDate)
})

const formattedDueDate = computed(() => {
  if (props.invoice.dueDate) {
    return formatDate(props.invoice.dueDate)
  }
  
  // Only calculate due date if we have a purchase date
  if (props.invoice.purchaseDate) {
    const purchaseDate = new Date(props.invoice.purchaseDate.replace(',', ''))
    if (!isNaN(purchaseDate.getTime())) {
      const dueDate = new Date(purchaseDate)
      dueDate.setDate(purchaseDate.getDate() + 14)
      return formatDate(dueDate)
    }
  }
  
  return '' // Return empty if no valid dates
})

// Update days left calculation
const daysLeft = computed(() => {
  try {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    let dueDate
    if (props.invoice.dueDate) {
      // Parse the due date from German format
      const [day, month, year] = props.invoice.dueDate.split('.')
      dueDate = new Date(year, month - 1, day)
    } else {
      dueDate = new Date(formattedDueDate.value.split('.').reverse().join('-'))
    }
    
    const diffTime = dueDate.getTime() - today.getTime()
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)))
  } catch (e) {
    console.error('Error calculating days left:', e)
    return 14
  }
})
</script>

<style scoped>
/* A4 Paper styling - scale-paper avoids LightningCSS parse error with scale-[0.85] */
@media screen {
  .scale-paper {
    transform: scale(0.85);
    transform-origin: center;
  }
}

/* Print styles - paper-width/paper-min-height avoid LightningCSS parse error with bracket notation */
@media print {
  .paper-width {
    width: 210mm;
  }

  .paper-min-height {
    min-height: 297mm;
  }

  .scale-paper {
    transform: none;
  }
}

/* Add some print-friendly styles */
@media print {
  .bg-gray-50 {
    background-color: white !important;
  }
  
  .border {
    border-color: #e5e7eb !important;
  }
}
</style> 